/**
 * PINNED ISLANDS (V17's first rule; VC-D's pinned open, 2026-10-17): an
 * island a holder's open answers AT its pin (`at: <commit>`, `lane: null`,
 * no tail) is that version on the page, never the head. The client keeps
 * it fixed (DAGSyncClient: no subscribe, no catch-up, no frame) and says so
 * on its computed fields: the pinned open serves none
 * (`computed: {unavailable: "not_evaluated_at_this_version"}`).
 *
 * Kept per island while some open holds it pinned; it is forgotten when the
 * island is released or answered live again.
 */

import { useSyncExternalStore } from "react";

export type PinnedIsland = { at: string; computedUnavailable: boolean };

const byIsland = new Map<string, PinnedIsland>();
const listeners = new Set<() => void>();

const notify = () => { for (const l of Array.from(listeners)) l(); };

export function setPinnedIsland(island: string, at: string, computedUnavailable: boolean): void {
  const cur = byIsland.get(island);
  if (cur && cur.at === at && cur.computedUnavailable === computedUnavailable) return;
  byIsland.set(island, { at, computedUnavailable });
  notify();
}

export function forgetPinnedIsland(island: string): void {
  if (byIsland.delete(island)) notify();
}

export function pinnedIsland(island: string | null | undefined): PinnedIsland | null {
  return (island && byIsland.get(island)) || null;
}

export function usePinnedIsland(island: string | null | undefined): PinnedIsland | null {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => pinnedIsland(island),
    () => null,
  );
}

/** The pinned open's computed: `{unavailable: …}` in place of channel URLs. */
export function computedUnavailable(computed: unknown): boolean {
  return !!computed && typeof computed === "object" && typeof (computed as { unavailable?: unknown }).unavailable === "string";
}

export function resetPinnedIslandsForTests(): void {
  byIsland.clear();
  notify();
}
